import { doc, getDoc, setDoc, updateDoc, onSnapshot, Unsubscribe } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { NotificationPreferences, defaultPreferences } from '@/types/notifications';

export class PreferencesService {
  private static instance: PreferencesService;
  private cache: Map<string, NotificationPreferences> = new Map();
  private listeners: Map<string, Unsubscribe> = new Map();

  static getInstance(): PreferencesService {
    if (!PreferencesService.instance) {
      PreferencesService.instance = new PreferencesService();
    }
    return PreferencesService.instance;
  }

  /**
   * Deep merge preferences with defaults to ensure all fields are present
   */
  private mergeWithDefaults(loadedPrefs: Partial<NotificationPreferences>): NotificationPreferences {
    return {
      dailySummary:{
        ...defaultPreferences.dailySummary,
        ...loadedPrefs.dailySummary,
      },
      whatsapp: {
        ...defaultPreferences.whatsapp,
        ...loadedPrefs.whatsapp,
      },
      email: {
        ...defaultPreferences.email,
        ...loadedPrefs.email,
      },
      webPush: {
        ...defaultPreferences.webPush,
        ...loadedPrefs.webPush,
      },
    };
  }

  /**
   * Get user preferences document reference
   */
  private getUserPrefsRef(userId: string) {
    return doc(db, `users/${userId}/settings/notifications`);
  }

  /**
   * Load preferences for a user with automatic migration
   */
  async loadPreferences(userId: string): Promise<NotificationPreferences> {
    try {
      const userPrefsRef = this.getUserPrefsRef(userId);
      const prefsDoc = await getDoc(userPrefsRef);
      
      let preferences: NotificationPreferences;
      
      if (prefsDoc.exists()) {
        const loadedData = prefsDoc.data();
        preferences = this.mergeWithDefaults(loadedData);
        
        // Check if migration is needed (missing fields)
        const needsMigration = this.needsMigration(loadedData, preferences);
        if (needsMigration) {
          console.log('Migrating preferences document for user:', userId);
          await this.savePreferences(userId, preferences);
        }
      } else {
        // Create new document with defaults
        preferences = { ...defaultPreferences };
        await this.savePreferences(userId, preferences);
      }
      
      // Cache the result
      this.cache.set(userId, preferences);
      return preferences;
    } catch (error) {
      console.error('Error loading preferences:', error);
      throw new Error('Failed to load notification preferences');
    }
  }

  /**
   * Check if preferences document needs migration
   */
  private needsMigration(loadedData: Partial<NotificationPreferences>, mergedPrefs: NotificationPreferences): boolean {
    // Check if any required fields are missing, including newly added dailySummary
    return (
      !loadedData.dailySummary ||
      typeof loadedData.dailySummary?.whatsappEnabled === 'undefined' ||
      !loadedData.webPush ||
      typeof loadedData.webPush?.enabled === 'undefined' ||
      !loadedData.email ||
      typeof loadedData.email?.enabled === 'undefined' ||
      !loadedData.whatsapp ||
      typeof loadedData.whatsapp?.isVerified === 'undefined'
    );
  }

  /**
   * Save preferences to Firestore
   */
  async savePreferences(userId: string, preferences: NotificationPreferences): Promise<void> {
    try {
      const userPrefsRef = this.getUserPrefsRef(userId);
      await setDoc(userPrefsRef, preferences);
      
      // Update cache
      this.cache.set(userId, preferences);
    } catch (error) {
      console.error('Error saving preferences:', error);
      throw new Error('Failed to save notification preferences');
    }
  }

  /**
   * Update specific WhatsApp preferences
   */
  async updateWhatsAppPreferences(
    userId: string, 
    updates: Partial<NotificationPreferences['whatsapp']>
  ): Promise<NotificationPreferences> {
    try {
      const currentPrefs = await this.loadPreferences(userId);
      const updatedPrefs: NotificationPreferences = {
        ...currentPrefs,
        whatsapp: {
          ...currentPrefs.whatsapp,
          ...updates,
        },
      };
      
      await this.savePreferences(userId, updatedPrefs);
      return updatedPrefs;
    } catch (error) {
      console.error('Error updating WhatsApp preferences:', error);
      throw new Error('Failed to update WhatsApp preferences');
    }
  }

  /**
   * Update specific email preferences
   */
  async updateEmailPreferences(
    userId: string, 
    updates: Partial<NotificationPreferences['email']>
  ): Promise<NotificationPreferences> {
    try {
      const currentPrefs = await this.loadPreferences(userId);
      const updatedPrefs: NotificationPreferences = {
        ...currentPrefs,
        email: {
          ...currentPrefs.email,
          ...updates,
        },
      };
      
      await this.savePreferences(userId, updatedPrefs);
      return updatedPrefs;
    } catch (error) {
      console.error('Error updating email preferences:', error);
      throw new Error('Failed to update email preferences');
    }
  }

  /**
   * Update specific web push preferences
   */
  async updateWebPushPreferences(
    userId: string, 
    updates: Partial<NotificationPreferences['webPush']>
  ): Promise<NotificationPreferences> {
    try {
      const currentPrefs = await this.loadPreferences(userId);
      const updatedPrefs: NotificationPreferences = {
        ...currentPrefs,
        webPush: {
          ...currentPrefs.webPush,
          ...updates,
        },
      };
      
      await this.savePreferences(userId, updatedPrefs);
      return updatedPrefs;
    } catch (error) {
      console.error('Error updating web push preferences:', error);
      throw new Error('Failed to update web push preferences');
    }
  }

  /**
   * Subscribe to real-time preferences updates
   */
  subscribeToPreferences(
    userId: string, 
    callback: (preferences: NotificationPreferences) => void
  ): Unsubscribe {
    const userPrefsRef = this.getUserPrefsRef(userId);
    
    const unsubscribe = onSnapshot(userPrefsRef, (doc) => {
      if (doc.exists()) {
        const loadedData = doc.data();
        const preferences = this.mergeWithDefaults(loadedData);
        this.cache.set(userId, preferences);
        callback(preferences);
      } else {
        // Document doesn't exist, use defaults
        const preferences = { ...defaultPreferences };
        callback(preferences);
      }
    }, (error) => {
      console.error('Error in preferences subscription:', error);
    });

    // Store the unsubscribe function
    this.listeners.set(userId, unsubscribe);
    return unsubscribe;
  }

  /**
   * Unsubscribe from preferences updates
   */
  unsubscribeFromPreferences(userId: string): void {
    const unsubscribe = this.listeners.get(userId);
    if (unsubscribe) {
      unsubscribe();
      this.listeners.delete(userId);
    }
  }

  /**
   * Get cached preferences (if available)
   */
  getCachedPreferences(userId: string): NotificationPreferences | null {
    return this.cache.get(userId) || null;
  }

  /**
   * Clear cache for a user
   */
  clearCache(userId: string): void {
    this.cache.delete(userId);
  }

  /**
   * Clear all caches and listeners
   */
  clearAll(): void {
    // Unsubscribe from all listeners
    this.listeners.forEach((unsubscribe) => unsubscribe());
    this.listeners.clear();
    
    // Clear cache
    this.cache.clear();
  }

  async updateDailySummaryPreferences(
    userId: string,
    updates: Partial<NotificationPreferences['dailySummary']>
  ): Promise<NotificationPreferences> {
    try {
      const currentPrefs = await this.loadPreferences(userId);
      const updatedPrefs: NotificationPreferences = {
        ...currentPrefs,
        dailySummary: {
          ...currentPrefs.dailySummary,
          ...updates,
        },
      };

      await this.savePreferences(userId, updatedPrefs);
      return updatedPrefs;
    } catch (error) {
      console.error('Error updating daily summary preferences:', error);
      throw new Error('Failed to update daily summary preferences');
    }
  }
}

// Export singleton instance
export const preferencesService = PreferencesService.getInstance();